import { TSitecoreContext } from '../../../types/sitecore.types';
import { ImageField, LinkField, StringField, THeadingTag, VideoSitecoreField } from '../../../types/types';
import { SplitModuleContainerProps } from '../../@shared/splitModuleContainer/types/splitModuleContainer.types';
import {
  TDefaultCardImageProps,
  TSpecialOffersFields,
  TSpecialOffersParams,
  TSpecialOffersProps,
  TSpecialOffersSlideFields,
  TSpecialOffersSlideProps,
} from '../types/cardCarousel.types';

export class SpecialOffersSlideModel {
  private readonly fields?: TSpecialOffersSlideFields;
  private readonly id?: string;

  constructor(props: TSpecialOffersSlideProps) {
    this.fields = props?.fields;
    this.id = props?.id;    
  }

  get cardId(): string {
    return this.id ?? '';
  }

  get type(): StringField | undefined {
    return this.fields?.cardType?.fields?.Key;
  }

  get title(): StringField | undefined {
    return this.fields?.title;
  }

  get subtitle(): StringField | undefined {
    return this.fields?.subtitle;
  }

  get label(): StringField | undefined {
    return this.fields?.label;
  }

  get badgeLink(): LinkField | undefined {
    return this.fields?.badgeLink;
  }

  get desktopImage(): ImageField | undefined {
    return this.fields?.image;
  }

  get mobileImage(): ImageField | undefined {
    return this.fields?.mobileImage;
  }

  get buttonPrimary(): LinkField | undefined {
    return this.fields?.buttonPrimary;
  }

  get buttonSecondary(): LinkField | undefined {
    return this.fields?.buttonSecondary;
  }

  get validButtonPrimaryExists(): boolean {
    return !!(this.buttonPrimary?.value?.text && this.buttonPrimary?.value?.href);
  }

  get validButtonSecondaryExists(): boolean {
    return !!(this.buttonSecondary?.value?.text && this.buttonSecondary?.value?.href);
  }

  get downloadButtonLink(): LinkField | undefined {
    return this.fields?.downloadButtonLink;
  }

  get useVideo(): boolean {
    return this.fields?.useVideo?.value || false;
  }

  get video(): VideoSitecoreField | undefined {
    return this.fields?.video;
  }

  get showPopupWithSplitModule(): boolean {
    return (
      (!this.buttonPrimary || (!this.buttonPrimary?.value?.url && !this.buttonPrimary?.value?.href)) &&
      !!this.fields?.items?.[0].fields?.items?.[0] &&
      !!this.splitModuleLabel
    );
  }

  get splitModules(): SplitModuleContainerProps[] {
    return this.fields?.items?.[0]?.fields?.items as SplitModuleContainerProps[];
  }

  get splitModuleLabel(): string | undefined {    
    return this.fields?.items?.[0]?.fields?.popUpLabel?.value;
  }
}

export class SpecialOffersModel {
  private readonly fields: null | TSpecialOffersFields;
  private readonly params: null | TSpecialOffersParams;
  private readonly context: TSitecoreContext | undefined;

  constructor(props: TSpecialOffersProps) {
    this.fields = props?.fields || null;
    this.params = props?.params || null;
    this.context = props?.sitecoreContext;
  }

  get eyebrow(): StringField | undefined {
    return this.fields?.eyebrow;
  }

  get link(): LinkField | undefined {
    return this.fields?.link;
  }

  get title(): StringField | undefined {
    return this.fields?.title;
  }

  get subtitle(): StringField | undefined {
    return this.fields?.subtitle;
  }

  get desktopImage(): ImageField | undefined {
    return this.fields?.image;
  }

  get mobileImage(): ImageField | undefined {
    return this.fields?.mobileImage;
  }

  get defaultBgDesktopImage(): ImageField | undefined {
    return {
      value: {
        src: this.context?.defaultBackgroundImageParameters?.desktopImageData?.cardCarousel,
      },
    };
  }

  get defaultBgMobileImage(): ImageField | undefined {
    return {
      value: {
        src: this.context?.defaultBackgroundImageParameters?.mobileImageData?.cardCarousel,
      },
    };
  }

  get desktopBgImage(): ImageField | undefined {
    return this.desktopImage?.value?.src ? this.desktopImage : this.defaultBgDesktopImage;
  }

  get mobileBgImage(): ImageField | undefined {
    return this.mobileImage?.value?.src ? this.mobileImage : this.defaultBgMobileImage;
  }

  get defaultCardDesktopImage(): ImageField | undefined {
    return {
      value: {
        src: this.fields?.cardCarouselPlaceholderImageParameters?.desktopImageData?.url,
      },
    };
  }

  get defaultCardMobileImage(): ImageField | undefined {
    return {
      value: {
        src: this.fields?.cardCarouselPlaceholderImageParameters?.mobileImageData?.url,
      },
    };
  }

  get defaultCardImage(): TDefaultCardImageProps | undefined {
    return {
      desktopImage: this.defaultCardDesktopImage,
      mobileImage: this.defaultCardMobileImage,
    };
  }

  get items(): SpecialOffersSlideModel[] {
    if (this.fields && this.fields?.items) {
      return this.fields?.items.map((Card: TSpecialOffersSlideProps) => {
        return new SpecialOffersSlideModel(Card);
      });
    } else {
      return [];
    }
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get headingTag(): THeadingTag {
    return (this.params?.headingTag?.toLowerCase() as THeadingTag) || 'h2';
  }
}
